'use client';
// X402Receipts — public "real money received" panel (v2.15.2).
//
// Founder mandate: a REAL payment must be VISIBLE on the platform the moment
// it settles — not buried in an admin ledger. This panel proves the x402
// economy with live data:
//   • USDC received (all time)   — from the durable settlement ledger
//   • Settlement vault balance   — LIVE from Base RPC (the actual on-chain
//     USDC sitting in our dedicated vault, 0x2152…5629)
//   • the settlement tape        — every real payment with its Basescan /
//     on-chain proof link, across BOTH rails (Base USDC + native AMAN).
// Strictly read-only: this component never signs, moves or mints anything.
import { Coins, Globe, Vault, Bot, Radio } from 'lucide-react';
import { fetchJson, fmtInt, Mono, timeAgo, SectionHeader, StatCard, usePoll } from './shared';

interface Settlement {
  scheme: 'aman-x402' | 'exact';
  facilitator: 'node-native' | 'cdp';
  serviceId: string;
  serviceName?: string;
  txId?: string;
  transaction?: string;
  net?: string;
  caller: string | null;
  payTo?: string;
  amountAman?: number;
  amountUsd?: number;
  amountMicros?: string;
  settledAt: number;
}
interface PaymentsView {
  stats: { total: number; native: number; base: number; totalUsd: number; totalAman: number; distinctPayers: number; lastAt: number | null };
  settlements: Settlement[];
  vault: { address: string; usdcMicros: string; usdc: number; live: boolean; checkedAt: number };
}

const BASESCAN = 'https://basescan.org';

export function X402Receipts({ net }: { net: string }) {
  const { data } = usePoll<PaymentsView>(
    async () => fetchJson<PaymentsView>(`/api/x402/payments?net=${net}`),
    10_000, [net],
  );

  const stats = data?.stats;
  const vault = data?.vault;
  const tape = data?.settlements ?? [];

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={Coins}
        title="Real money received — the x402 economy is live"
        desc="Every settlement on both rails, with its on-chain proof. The vault balance is read LIVE from Base — nothing is simulated, nothing is planted: an entry exists only because funds actually moved."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Globe} label="USDC received (Base)" accent="sky"
          value={`$${(stats?.totalUsd ?? 0).toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`}
          sub={`${fmtInt(stats?.base ?? 0)} settlement(s) via Coinbase facilitator`} />
        <StatCard icon={Vault} label="Settlement vault — live on Base" accent={vault?.live ? 'emerald' : 'rose'}
          value={vault ? <>{vault.usdc.toLocaleString('en-US', { maximumFractionDigits: 6 })} <span className="text-xs text-muted-foreground">USDC</span></> : '…'}
          sub={vault?.live ? 'on-chain balanceOf — real funds in our vault' : 'checking Base RPC…'} />
        <StatCard icon={Bot} label="Paying agents" accent="violet"
          value={fmtInt(stats?.distinctPayers ?? 0)}
          sub={stats?.lastAt ? `last payment ${timeAgo(stats.lastAt)}` : 'no payments yet'} />
        <StatCard icon={Coins} label="AMAN received (native)" accent="gold"
          value={<>{(stats?.totalAman ?? 0).toLocaleString('en-US', { maximumFractionDigits: 4 })} <span className="text-xs text-muted-foreground">AMAN</span></>}
          sub={`${fmtInt(stats?.native ?? 0)} call(s) settled in consensus`} />
      </div>

      {/* vault proof strip */}
      {vault && (
        <div className="glass p-4 space-y-2">
          <div className="text-xs font-bold flex items-center gap-2 flex-wrap">
            Settlement vault (self-custody, Base) — funds land here:
            <Mono className="text-[0.66rem]">{vault.address}</Mono>
          </div>
          <a href={`${BASESCAN}/address/${vault.address}`} target="_blank" rel="noreferrer"
            className="text-[0.66rem] text-sky-400 hover:underline inline-flex items-center gap-1">
            <Globe className="size-3" /> Verify the vault balance on Basescan
          </a>
        </div>
      )}

      {/* settlement tape */}
      <div className="glass p-4">
        <div className="flex items-center gap-2 text-sm font-bold mb-3"><Radio className="size-4 text-rose-400" /> Settlement tape — every real payment</div>
        {tape.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            No paid calls yet — the first real payment (from any x402 client or the First-Agent panel above) appears here with its tx hash.
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {tape.map((s, i) => {
              const isBase = s.scheme === 'exact';
              const txHash = s.transaction ?? s.txId ?? '';
              return (
                <div key={`${txHash}-${i}`} className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <div className="font-bold flex items-center gap-1.5 flex-wrap">
                      <span className={`rounded-full px-2 py-0.5 text-[0.58rem] font-black ${isBase ? 'bg-sky-500/10 text-sky-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                        {isBase ? 'USDC · Base' : 'AMAN · native'}
                      </span>
                      {s.serviceName || s.serviceId}
                    </div>
                    <Mono className="text-[0.62rem] text-muted-foreground">
                      {txHash.slice(0, 18)}…{txHash.slice(-8)} · from {(s.caller ?? '—').slice(0, 10)}… · {timeAgo(s.settledAt)}
                    </Mono>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`font-black whitespace-nowrap ${isBase ? 'text-sky-400' : 'text-primary'}`} dir="ltr">
                      {isBase ? `$${(s.amountUsd ?? 0).toFixed(6)}` : `${(s.amountAman ?? 0).toLocaleString('en-US', { maximumFractionDigits: 4 })} AMAN`}
                    </span>
                    {isBase && txHash ? (
                      <a href={`${BASESCAN}/tx/${txHash}`} target="_blank" rel="noreferrer"
                        className="rounded-lg bg-sky-500/10 text-sky-400 ring-1 ring-sky-500/30 px-2 py-1 text-[0.6rem] font-black">Basescan</a>
                    ) : (
                      <span className="rounded-lg bg-muted text-muted-foreground px-2 py-1 text-[0.6rem] font-bold">on-chain — search in Explorer</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
